import { differenceInCalendarDays, parse } from 'date-fns';

/** Parse the YYYY-MM-DD date part of a date string to a local Date (time-of-day discarded). */
function parseDatePart(dateStr: string): Date {
  // Take the date prefix so both bare 'YYYY-MM-DD' and ISO datetimes ('...THH:mm:ssZ') are treated as the
  // calendar date as written — avoids timezone-offset shifting a 'Z' datetime onto a different local day.
  return parse(dateStr.slice(0, 10), 'yyyy-MM-dd', new Date());
}

/**
 * Days between two date strings (bare 'YYYY-MM-DD' or ISO datetimes). Clamped to 0 if negative.
 *
 * Uses date-fns `differenceInCalendarDays`, which counts calendar-day boundaries (time-of-day stripped),
 * so it's DST-safe — unlike dividing an epoch-ms difference, which goes off-by-one across a DST transition
 * (e.g. 2025-03-01→2025-03-31 would yield 29 instead of 30). See expenseInterest.spec for the regression tests.
 */
export function daysBetween(expenseDateStr: string, asOfDateStr: string): number {
  return Math.max(0, differenceInCalendarDays(parseDatePart(asOfDateStr), parseDatePart(expenseDateStr)));
}

/**
 * Interest for a single expense in dollars.
 * Uses floor() per spec BR-V2-9 for client-favorable rounding.
 * Converts to integer cents first to avoid IEEE 754 mid-calculation rounding.
 * Formula: floor(amount_cents × rate_pct × days / 36500) / 100
 * Canonical: $1,000 × 10% × 181 days → floor(100000×10×181/36500)/100 = $49.58
 * @param amount Expense amount in dollars
 * @param rate   Company expenseInterest percentage (e.g. 10 for 10%)
 * @param days   Days between expense date and as-of date
 */
export function computeExpenseInterest(amount: number, rate: number, days: number): number {
  const amountCents = Math.round(amount * 100);
  return Math.floor(amountCents * rate * days / 36500) / 100;
}
